<template>
	<div>
		<div v-if="!isLoading">
			<v-container grid-list-xl fluid>
				<v-snackbar v-model="prDeletedNotif" top>
					Performance Review Deleted
					<v-btn color="pink" flat @click="prDeletedNotif = false">
						Close
					</v-btn>
				</v-snackbar>

				<v-snackbar v-model="goalDeletedNotif" top>
					Goal Deleted Successfully
					<v-btn color="pink" flat @click="goalDeletedNotif = false">
						Close
					</v-btn>
				</v-snackbar>

				<v-snackbar v-model="goalSavedNotif" top>
					Goal Saved
					<v-btn color="pink" flat @click="goalSavedNotif = false">
						Close
					</v-btn>
				</v-snackbar>

				<v-snackbar v-model="checkInDeletedNotif" top>
					Check In Deleted
					<v-btn color="pink" flat @click="checkInDeletedNotif = false">
						Close
					</v-btn>
				</v-snackbar>

				<v-alert v-if="loggedInEmployee.isAdmin && isBillingEnabled && !loggedInSubscription" color="info" icon="mdi-bell" :value="true" outline class="text-xs-left">
					<strong>Billing Update:</strong> We’ve made managing your payments easier! <strong><router-link :to="{ name: 'billing' }">Set up your online payment plan now to ensure uninterrupted service</router-link></strong
					>.
				</v-alert>
				<billing-plan-cancellation-pending-message v-else-if="loggedInEmployee.isAdmin && isBillingEnabled && isSubscriptionPendingCancellation" class="mb-4"/>

				<v-layout row wrap>
					<v-flex md6 v-if="visibleToDoList && visibleToDoList.length > 0">
						<employee-to-do-list :employeeToDoList="visibleToDoList" :loggedInEmployee="loggedInEmployee" />
					</v-flex>
					<v-flex md6 v-if="showSelfAssignableFormsCard">
						<v-card class="mb-4">
							<v-toolbar flat dense>
								<v-toolbar-title class="subheading">Start a form</v-toolbar-title>
								<v-spacer></v-spacer>
								<v-btn small color="primary" :loading="isStartingSelfAssignment" @click="openSelfAssignableForms">
									Start Form
								</v-btn>
							</v-toolbar>
							<v-divider></v-divider>
							<v-card-text class="text-xs-left">
								<div class="body-2 mb-1">{{ selfAssignableFormsCardTitle }}</div>
								<div class="caption text--secondary">{{ selfAssignableFormsCardSubtitle }}</div>
							</v-card-text>
						</v-card>
					</v-flex>
					<v-flex md6 v-if="loggedInEmployee.isAdmin && myMessages.length > 0">
						<home-messages-card :messages="myMessages" />
					</v-flex>
					<v-flex md6 v-if="$_hasAccessToModule('goals')">
						<employee-goal-list :employee-id="loggedInEmployee.id" :goals="goals" header-title="My Goals" :flat="false" @onGoalAdded="onGoalAdded" @onGoalEdited="onGoalEdited" @onGoalDeleted="onGoalDeleted"></employee-goal-list>
					</v-flex>
					<v-flex xl12 v-if="$_hasAccessToModule('checkIns')">
						<v-card class="mb-4">
							<v-toolbar flat dense cad>
								<v-toolbar-title class="subheading">My Pending Check Ins</v-toolbar-title>
								<v-spacer></v-spacer>
							</v-toolbar>
							<v-divider></v-divider>

							<v-data-table disable-initial-sort :headers="checkInHeaders" :items="myCheckIns" :loading="checkIns_loading" hide-actions class="elevation-1">
								<v-progress-linear slot="progress" color="blue" indeterminate></v-progress-linear>

								<template slot="items" slot-scope="props">
									<tr :class="{ complete: props.item.status === 'Complete' }">
										<td class="text-xs-left">
											<router-link :to="{ name: 'employee', params: { id: props.item.employeeId } }"
												><span :class="{ highlight: props.item.isLoggedInEmployee }">{{ props.item.employee }}</span></router-link
											>
										</td>
										<td class="text-xs-left">
											<router-link :to="{ name: 'employee', params: { id: props.item.reviewerId } }"
												><span :class="{ highlight: props.item.isLoggedInReviewer }">{{ props.item.reviewer }}</span></router-link
											>
										</td>
										<td class="text-xs-left">
											<AppDate :date="props.item.createdDateUtc" />
										</td>
										<td class="text-xs-left">
											<AppDate :date="props.item.dueDateUtc" showDate nullDate="N/A" />
										</td>
										<td class="text-xs-left">
											<div v-if="props.item.status === 'Complete'" style="color: #388E3C">
												<div v-if="props.item.updatedDateUtc != null">
													Submitted
													<AppDate :date="props.item.updatedDateUtc" />
												</div>
												<div v-else>
													Complete
												</div>
											</div>
											<div v-else>
												<div v-if="props.item.updatedDateUtc != null" style="color: #fb8c00;">
													Draft saved
													<AppDate :date="props.item.updatedDateUtc" />
												</div>
												<div v-else>
													{{ props.item.status }}
												</div>
											</div>
										</td>
										<td class="text-xs-right px-0" style="width: 1px;">
											<v-btn small :class="{ primary: props.item.isLoggedInEmployee && props.item.status !== 'Complete' }" @click="viewCheckIn(props.item)">{{ getCheckInsViewButtonText(props.item) }}</v-btn>
										</td>
										<td class="justify-center pl-0 pr-2" style="width: 1px;">
											<v-icon v-if="loggedInEmployee != null && loggedInEmployee.isAdmin" small @click="deleteCheckIn(props.item)">
												delete
											</v-icon>
										</td>
									</tr>
								</template>
							</v-data-table>
						</v-card>
					</v-flex>
					<v-flex xl12 v-if="$_hasAccessToModule('performanceReviews')">
						<v-card class="mb-4">
							<v-toolbar flat dense cad>
								<v-toolbar-title class="subheading">My Pending Reviews</v-toolbar-title>
								<v-spacer></v-spacer>
							</v-toolbar>
							<v-divider></v-divider>

							<v-data-table disable-initial-sort :headers="headers" :items="myReviews" :loading="performanceReviews_loading" hide-actions class="elevation-1">
								<v-progress-linear slot="progress" color="blue" indeterminate></v-progress-linear>

								<template slot="items" slot-scope="props">
									<tr :active="props.item.isLoggedInEmployee || props.item.isLoggedInReviewer">
										<td class="text-xs-left">{{ props.item.name }}</td>
										<td class="text-xs-left">
											<router-link :to="{ name: 'employee', params: { id: props.item.employeeId } }"
												><span :class="{ highlight: props.item.isLoggedInEmployee }">{{ props.item.employee }}</span></router-link
											>
										</td>
										<td class="text-xs-left">{{ props.item.positionTitle }}</td>
										<td class="text-xs-left">
											<router-link :to="{ name: 'employee', params: { id: props.item.reviewerId } }"
												><span :class="{ highlight: props.item.isLoggedInReviewer }">{{ props.item.reviewer }}</span></router-link
											>
										</td>
										<td class="text-xs-left">
											<v-tooltip bottom v-if="isInPast(props.item.scheduledDate)">
												<div slot="activator">
													<v-icon color="#f44336" small>mdi-alert-circle-outline</v-icon>
													{{ props.item.scheduledDate | formatDate() }}
												</div>
												<span>Due <AppDate :date="props.item.scheduledDate"/></span>
											</v-tooltip>
											<span v-else>{{ props.item.scheduledDate | formatDate() }}</span>
										</td>
										<td class="text-xs-left" :style="{ color: props.item.status === 'Discussion' ? '#388E3C' : props.item.status === 'InProgress' ? '#fb8c00' : '' }">{{ getUserFriendlyStatus(props.item.status) }}</td>
										<td class="text-xs-right px-0">
											<v-btn small @click="view(props.item)">View</v-btn>
										</td>
										<td class="justify-center pl-0 pr-2" style="width: 1px;">
											<v-icon v-if="loggedInEmployee != null && loggedInEmployee.isAdmin" small @click="deletePerformanceReview(props.item)">
												delete
											</v-icon>
										</td>
									</tr>
								</template>
							</v-data-table>
						</v-card>
					</v-flex>
				</v-layout>
			</v-container>
		</div>

		<loading-indicator v-if="isLoading" loading-text="Loading Dashboard..." />

		<self-assignable-form-dialog
			v-if="showSelfAssignableDialog"
			:dialog="showSelfAssignableDialog"
			:forms="selfAssignableForms"
			:is-starting="isStartingSelfAssignment"
			@start="startSelfAssignment"
			@close="showSelfAssignableDialog = false"
		/>

		<embedded-forms-app-dialog
			v-if="embeddedForms.dialog"
			:dialog="embeddedForms.dialog"
			:title="embeddedForms.title"
			:path="embeddedForms.path"
			@close="closeEmbeddedForms"
			@authLoginStarted="onEmbeddedFormsAuthStarted"
			@authLoginFailed="onEmbeddedFormsAuthFailed"
			@submissionCompleted="onEmbeddedSubmissionCompleted"
		/>
	</div>
</template>

<script>
	import moment from 'moment';
	import { mapState, mapGetters } from 'vuex';
	import EmployeeGoalList from '@/components/employee/EmployeeGoalList';
	import LoadingIndicator from '@/components/common/LoadingIndicator';
	import DailyScrumPanel from '@/components/dailyScrum/DailyScrumPanel';
	import HomeMessagesCard from '@/components/HomeMessagesCard';
	import EmployeeToDoList from '@/components/employee/EmployeeToDoList';
	import BillingPlanCancellationPendingMessage from '@/components/settings/billing/BillingPlanCancellationPendingMessage';
	import EmbeddedFormsAppDialog from '@/components/forms/EmbeddedFormsAppDialog.vue';
	import SelfAssignableFormDialog from '@/components/forms/SelfAssignableFormDialog.vue';
	import performanceReviewStatusMixin from '@/mixins/performanceReviewStatusMixin';
	import FeatureFlag from '@/enums/featureFlag';
	import productPlanStatus from '@/enums/productPlanStatus';
	import toast from '@/services/toasts/index.js';

	export default {
		name: 'Home',
		components: {
			EmployeeGoalList,
			LoadingIndicator,
			DailyScrumPanel,
			HomeMessagesCard,
			EmployeeToDoList,
			BillingPlanCancellationPendingMessage,
			EmbeddedFormsAppDialog,
			SelfAssignableFormDialog
		},
		data() {
			return {
				prDeletedNotif: false,
				headers: [
					{ text: 'Type', value: 'name' },
					{ text: this.$term('employee', { case: 'title' }), value: 'employee' },
					{ text: 'Job Title', value: 'positionTitle' },
					{ text: 'Reviewer', value: 'reviewer' },
					{ text: 'Scheduled Date', value: 'scheduledDateUTC', sortable: false },
					{ text: 'Status', value: 'status' },
					{ text: '', value: 'actions', sortable: false }
				],
				employee: null,
				goalSavedNotif: false,
				goalDeletedNotif: false,
				checkInDeletedNotif: false,
				isLoadingMyToDoList: true,
				isLoadingEmployee: true,
				showSelfAssignableDialog: false,
				embeddedForms: {
					dialog: false,
					title: 'Complete Form',
					path: ''
				}
			};
		},
		mixins: [performanceReviewStatusMixin],
		computed: {
			...mapGetters({
				loggedInEmployee: 'loggedInEmployee',
				loggedInTenant: 'loggedInTenant',
				myReviews: 'performanceReviews/performanceReviewsMyList',
				myCheckIns: 'checkIns/checkInsMyPendingList',
				myMessages: 'myMessages',
				myToDoList: 'myToDoList',
				loggedInSubscription: 'loggedInSubscription',
				selfAssignableForms: 'forms/selfAssignableForms',
				isStartingSelfAssignment: 'forms/isStartingSelfAssignment'
			}),
			...mapState({
				performanceReviews_loading: state => state.performanceReviews.performanceReviews_loading,
				checkIns_loading: state => state.checkIns.checkIns_loading
			}),
			goals() {
				return this.employee !== null ? this.employee.goals : [];
			},
			visibleToDoList() {
				const items = Array.isArray(this.myToDoList) ? this.myToDoList : [];
				if (this.isFormsEnabled) return items;
				return items.filter(item => item && item.type !== 'form_assignment');
			},
			checkInHeaders() {
				let h = [
					{ text: this.$term('employee', { case: 'title' }), value: 'employee' },
					{ text: 'Reviewer', value: 'reviewer' },
					{ text: 'Created', value: 'createdDateUtc' },
					{ text: 'Due Date', value: 'dueDateUtc' },
					{ text: 'Status', value: 'status' }
				];
				h.push({ text: '', value: 'actions', sortable: false });
				return h;
			},
			isLoading() {
				return this.isLoadingMyToDoList || this.isLoadingEmployee;
			},
			isBillingEnabled() {
				return this.$store.getters['features/isFeatureEnabled'](FeatureFlag.Billing);
			},
			isFormsEnabled() {
				return this.$store.getters['features/isFeatureEnabled'](FeatureFlag.Forms);
			},
			showSelfAssignableFormsCard() {
				return this.isFormsEnabled && this.selfAssignableForms.length > 0;
			},
			selfAssignableFormsCardTitle() {
				if (this.selfAssignableForms.length === 1) {
					return this.selfAssignableForms[0].name;
				}

				return `${this.selfAssignableForms.length} forms available`;
			},
			selfAssignableFormsCardSubtitle() {
				if (this.selfAssignableForms.length === 1) {
					return this.selfAssignableForms[0].description || 'Available now';
				}

				return this.selfAssignableForms
					.slice(0, 3)
					.map(form => form.name)
					.join(', ');
			},
			isSubscriptionPendingCancellation() {
				return this.loggedInSubscription && this.loggedInSubscription.status == productPlanStatus.CancelledPending;
			}
		},
		watch: {
			isFormsEnabled(value) {
				if (value) {
					this.loadSelfAssignableForms();
				}
			}
		},
		methods: {
			view(item) {
				this.$router.push(`/pr/${item.id}/`);
			},
			deletePerformanceReview(item) {
				confirm('Are you sure you want to delete this Performance Review?') &&
					this.$store.dispatch('performanceReviews/delete', item).then(
						response => {
							this.prDeletedNotif = true;
						},
						error => {
							console.error('Error deleting performance review');
						}
					);
			},
			onGoalAdded({ employeeId, goal }) {
				this.employee.goals.push(goal);
				this.goalSavedNotif = true;
			},
			onGoalEdited({ employeeId, goal }) {
				let foundGoal = this.employee.goals.find(x => x.id === goal.id);
				Object.assign(foundGoal, goal);
				this.goalSavedNotif = true;
			},
			onGoalDeleted({ employeeId, id }) {
				const index = this.employee.goals.indexOf(this.employee.goals.find(x => x.id == id));
				this.employee.goals.splice(index, 1);
				this.goalDeletedNotif = true;
			},
			getCheckInsViewButtonText(item) {
				if (item.isLoggedInEmployee && item.status !== 'Complete') return 'Fill out Check In';
				else return 'View';
			},
			async deleteCheckIn(item) {
				if (confirm(`Are you sure you want to delete this Check In?`)) {
					try {
						let result = await this.$store.dispatch('checkIns/delete', item);
						this.checkInDeletedNotif = true;
					} catch (err) {
						console.error('Error deleting Check In');
					}
				}
			},
			viewCheckIn(item) {
				this.$router.push(`/checkIn/${item.id}/`);
			},
			isInPast(dateUtc) {
				return moment.utc(dateUtc).isBefore(moment.utc());
			},
			async getMyToDoList() {
				this.isLoadingMyToDoList = true;

				try {
					await this.$store.dispatch('getMyToDoList');
				} finally {
					this.isLoadingMyToDoList = false;
				}
			},
			async getEmployee() {
				this.isLoadingEmployee = true;

				try {
					this.employee = await this.$store.dispatch('employees/getLoggedInEmployee');
				} finally {
					this.isLoadingEmployee = false;
				}
			},
			async loadSelfAssignableForms() {
				if (!this.isFormsEnabled) return;
				await this.$store.dispatch('forms/listSelfAssignable');
			},
			openSelfAssignableForms() {
				this.showSelfAssignableDialog = true;
			},
			getSubmissionId(result) {
				const data = result && result.data ? result.data : null;
				if (!data) return null;
				return data.id || data.submissionId || data.Id || data.SubmissionId;
			},
			async startSelfAssignment(form) {
				if (!form || !form.id) return;

				const result = await this.$store.dispatch('forms/startSelfAssignment', form.id);
				if (!result.isSuccess) return;

				this.showSelfAssignableDialog = false;
				this.openEmbeddedSubmission(this.getSubmissionId(result));
				await this.getMyToDoList();
			},
			openEmbeddedSubmission(submissionId) {
				if (!this.loggedInTenant || !submissionId) return;

				this.embeddedForms = {
					dialog: true,
					title: 'Complete Form',
					path: `/internal/forms/${this.loggedInTenant.id}/${submissionId}`
				};
			},
			closeEmbeddedForms() {
				this.embeddedForms.dialog = false;
			},
			onEmbeddedFormsAuthStarted() {
				toast.info('Signing you in to forms...');
			},
			onEmbeddedFormsAuthFailed(payload) {
				toast.error(payload && payload.message ? payload.message : 'Unable to sign you in to forms.');
			},
			async onEmbeddedSubmissionCompleted() {
				await this.getMyToDoList();
				toast.success('Form submission completed');
				this.closeEmbeddedForms();
			}
		},
		created() {
			this.$store.dispatch('setPageTitle', this.loggedInTenant.name);
			this.getMyToDoList();
			this.getEmployee();
			this.loadSelfAssignableForms();
		}
	};
</script>

<style lang="scss">
	@import '@/styles/_colors.scss';

	.pr-overdue {
		color: $red;
	}
</style>
